// Onglet Règlements : les paiements saisis dans Polaris, recopiés dans CIME Plus.
// Un avocat ne voit que les règlements des pièces qu'il a le droit de voir (RLS).
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Card } from '../ui/Card';
import { Spinner } from '../ui/Spinner';
import { FilterPill } from '../ui/FilterPill';
import { ListToolbar, ToolbarGrow, ToolbarSearch } from '../ui/ListToolbar';
import { FaCircleCheck, FaCircleExclamation, FaHourglass, FaMoneyCheckDollar } from '../ui/FaIcons';
import { cn } from '../../lib/cn';
import { formatAppDate } from '../../lib/dateFormat';
import { formatEuros } from '../../services/billing/billingService';
import {
  useBillingDocuments,
  useBillingPayments,
  useBillingPaymentsSync,
} from '../../hooks/queries/useBillingDocuments';
import { useDossiers } from '../../hooks/queries/useDossiers';
import {
  filterPayments,
  PAYMENT_BALANCE_LABELS,
  paymentBalance,
  paymentsKpis,
  type PaymentBalance,
} from '../../lib/billing/paymentsTab';
import { PAYMENT_METHOD_LABELS } from '../../lib/billing/polarPayments';

const NO_FILTERS = {};
const BALANCE_OPTIONS = (Object.keys(PAYMENT_BALANCE_LABELS) as PaymentBalance[]).map((value) => ({
  value,
  label: PAYMENT_BALANCE_LABELS[value],
}));

function clientName(client: { first_name: string | null; last_name: string | null; company_name: string | null } | null) {
  if (!client) return null;
  return client.company_name?.trim() || `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || null;
}

function Kpi({ icon, label, value, hint, tone }: { icon: ReactNode; label: string; value: string; hint: string; tone?: 'ok' | 'late' }) {
  return (
    <div className="grid grid-cols-[32px_minmax(0,1fr)] items-start gap-x-3 rounded-xl border border-neutral-200 bg-white px-4 py-3.5">
      <span className="grid h-8 w-8 place-items-center rounded-[9px] bg-[#f4f1e8] text-cime-text-muted [&_svg]:h-3.5 [&_svg]:w-3.5">{icon}</span>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">{label}</p>
        <b
          className={cn(
            'mt-[3px] block text-[22px] font-semibold tabular-nums',
            tone === 'ok' ? 'text-[#2f7a55]' : tone === 'late' ? 'text-cime-urgent' : 'text-cime-text-primary',
          )}
        >
          {value}
        </b>
        <small className="mt-0.5 block text-xs text-neutral-400">{hint}</small>
      </div>
    </div>
  );
}

export function BillingPaymentsTab({ onCount }: { onCount: (count: number) => void }) {
  const { data: docs } = useBillingDocuments(NO_FILTERS);
  const sync = useBillingPaymentsSync(docs);
  const { data: payments, isLoading } = useBillingPayments();
  const { data: dossiers } = useDossiers({ include_archived: true, include_closed: true });
  const [search, setSearch] = useState('');
  const [methods, setMethods] = useState<string[]>([]);
  const [balances, setBalances] = useState<string[]>([]);

  const dossierMap = useMemo(() => {
    const m = new Map<string, { number: string; title: string }>();
    for (const d of dossiers ?? []) m.set(d.id, { number: d.number, title: d.title });
    return m;
  }, [dossiers]);

  const rows = useMemo(
    () =>
      (payments ?? []).map((p) => {
        const dossier = dossierMap.get(p.document.dossier_id) ?? null;
        const client = clientName(p.document.client);
        const balance = paymentBalance(p.document);
        return {
          payment: p,
          dossier,
          client,
          balance,
          method: p.method,
          haystack: [p.method, p.note, p.document.number, p.document.notes, client, dossier?.number, dossier?.title]
            .filter(Boolean)
            .join(' ')
            .toLowerCase(),
        };
      }),
    [payments, dossierMap],
  );

  const visible = useMemo(() => filterPayments(rows, { q: search, methods, balances }), [rows, search, methods, balances]);
  const total = useMemo(() => visible.reduce((sum, r) => sum + Math.round(Number(r.payment.amount) * 100), 0) / 100, [visible]);
  const kpis = useMemo(() => paymentsKpis(payments ?? [], docs ?? [], new Date()), [payments, docs]);

  const methodOptions = useMemo(() => {
    const seen = new Set<string>(Object.values(PAYMENT_METHOD_LABELS));
    for (const r of rows) seen.add(r.method);
    return [...seen].map((m) => ({ value: m, label: m }));
  }, [rows]);

  useEffect(() => {
    if (!isLoading) onCount(visible.length);
  }, [visible.length, isLoading, onCount]);

  const filtersChanged = methods.length > 0 || balances.length > 0;
  const syncedAt = sync.data?.at;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Kpi
          icon={<FaCircleCheck />}
          label="Encaissé ce mois"
          value={formatEuros(kpis.monthAmount)}
          hint={`${kpis.monthCount} règlement${kpis.monthCount > 1 ? 's' : ''}`}
          tone="ok"
        />
        <Kpi
          icon={<FaHourglass />}
          label="Reste à encaisser"
          value={formatEuros(kpis.dueAmount)}
          hint={`${kpis.dueCount} facture${kpis.dueCount > 1 ? 's' : ''} impayée${kpis.dueCount > 1 ? 's' : ''}`}
        />
        <Kpi
          icon={<FaCircleExclamation />}
          label="Échu"
          value={formatEuros(kpis.lateAmount)}
          hint={`${kpis.lateCount} facture${kpis.lateCount > 1 ? 's' : ''} à relancer`}
          tone={kpis.lateAmount > 0 ? 'late' : undefined}
        />
      </div>

      <ListToolbar>
        <ToolbarSearch
          value={search}
          onChange={setSearch}
          placeholder="Rechercher par client, facture, dossier…"
          ariaLabel="Rechercher un règlement"
          className="w-[300px]"
        />
        <FilterPill label="Mode de paiement" options={methodOptions} selected={methods} onChange={setMethods} />
        <FilterPill label="Solde" options={BALANCE_OPTIONS} selected={balances} onChange={setBalances} />
        {filtersChanged && (
          <button
            type="button"
            onClick={() => {
              setMethods([]);
              setBalances([]);
            }}
            className="text-xs text-[#8a928c] underline underline-offset-2 hover:text-[#1f2823]"
          >
            Réinitialiser
          </button>
        )}
        <ToolbarGrow />
        <span className={cn('text-xs', sync.data?.unreachable ? 'text-cime-urgent' : 'text-neutral-400')}>
          {sync.isFetching
            ? 'Mise à jour depuis Polaris…'
            : sync.data?.unreachable
              ? 'Polaris injoignable : derniers règlements connus'
              : syncedAt
                ? `Mis à jour depuis Polaris ${formatDistanceToNow(new Date(syncedAt), { addSuffix: true, locale: fr })}`
                : 'Règlements saisis dans Polaris'}
        </span>
        <button
          type="button"
          onClick={() => void sync.refetch()}
          disabled={sync.isFetching || !docs}
          className="inline-flex h-8 items-center rounded-full border border-[#e6e2d8] bg-white px-3 text-[13px] font-semibold text-cime-text-primary hover:border-[#cfcab8] disabled:opacity-50"
        >
          Actualiser
        </button>
      </ListToolbar>

      {isLoading ? (
        <div className="flex justify-center py-12"><Spinner /></div>
      ) : visible.length === 0 ? (
        <Card>
          <div className="py-12 text-center text-neutral-500">
            <FaMoneyCheckDollar className="mx-auto mb-3 h-9 w-9 text-neutral-300" aria-hidden />
            <p className="text-sm">
              {rows.length > 0 ? 'Aucun règlement ne correspond à ces filtres.' : 'Aucun règlement pour l’instant.'}
            </p>
            {rows.length === 0 ? (
              <p className="mx-auto mt-2 max-w-md text-xs text-neutral-400">
                Les paiements se saisissent dans Polaris. Ils apparaissent ici à la prochaine mise à jour.
              </p>
            ) : null}
          </div>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="cime-table text-[13px]">
            <thead>
              <tr>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Règlement</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Dossier</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Client</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Facture réglée</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-right">Montant</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Solde</th>
                <th className="py-2 px-2.5 first:pl-[18px] last:pr-[18px] text-left">Reçu le</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(({ payment: p, dossier, client, balance }) => (
                <tr key={p.id}>
                  <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                    <span className="block text-[13px] font-medium text-cime-text-primary">{p.method}</span>
                    {p.note ? <span className="block text-[11px] text-neutral-400">{p.note}</span> : null}
                  </td>
                  <td className="max-w-[220px] px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                    {dossier ? (
                      <span title={dossier.title}>
                        <span className="block text-[11px] tracking-wide text-neutral-400">{dossier.number}</span>
                        <span className="block truncate text-[13px] text-cime-text-primary">{dossier.title}</span>
                      </span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-cime-text-primary">{client ?? <span className="text-neutral-400">—</span>}</td>
                  <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] leading-tight">
                    <Link to={`/facturation/${p.document.id}`} className="font-medium text-cime-primary hover:underline">
                      {p.document.number ?? 'Facture'}
                    </Link>
                    {p.document.issue_date ? (
                      <span className="block text-[11px] text-neutral-400">du {formatAppDate(p.document.issue_date, { year: false })}</span>
                    ) : null}
                  </td>
                  <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-right font-semibold tabular-nums text-cime-text-primary">
                    {formatEuros(p.amount)}
                  </td>
                  <td className="px-2.5 py-[9px] first:pl-[18px] last:pr-[18px]">
                    <span
                      className={cn(
                        'inline-flex h-[22px] items-center rounded-full px-2.5 text-xs font-semibold',
                        balance === 'full' ? 'bg-[rgba(47,122,85,0.1)] text-[#2f7a55]' : 'bg-cime-gold-bg text-cime-gold-text',
                      )}
                    >
                      {balance === 'full' ? 'Soldée' : 'Reste dû'}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-2.5 py-[9px] first:pl-[18px] last:pr-[18px] text-cime-text-primary">
                    {formatAppDate(p.paid_on)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between border-t border-[#e6e2d8] px-[18px] py-2.5 text-xs text-neutral-400">
            <span>
              {visible.length} règlement{visible.length > 1 ? 's' : ''} · {formatEuros(total)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
